/**
 * Technical Performance — asset-level operational lens.
 *
 * Counterpart to Commercial Performance: where Commercial answers "what did
 * we earn?", Technical answers "did the asset run as designed, and what
 * stopped it when it didn't?" — availability, capacity factor, curtailment,
 * dispatch compliance, and for batteries: RTE, cycles, SoC utilisation, FCAS
 * enablement, setpoint compliance.
 *
 * Layout (matches Commercial Performance shape):
 *   - Header row    : title + AssetPicker + PeriodPicker + refresh
 *   - Sub-header    : period chip + date range + active asset
 *   - KPI grid      : 3×2 tiles + curtailment waterfall, flavour-swapped
 *   - Main row      : BESS only — SoC heatmap + battery health card
 *   - Trend row     : dispatch compliance (gen; LTM CF trend is flag-hidden)
 *                     or cycles/RTE/FCAS (BESS)
 *   - Threshold     : production by clearing-price band (gen only — moved in
 *                     from the Spot Price tab, 2026-06-10 METIS review)
 *   - Deep dive     : BESS only — hour-of-day profile + setpoint histogram
 *
 * Curtailment detail (per-bucket breakdown, vs-spot-price scatter, binding
 * constraints) lives on the Curtailment tab; the generation timeline, asset
 * health card and the page-level scatter were trimmed 2026-06-11 so this
 * page stays a summary.
 *
 * Data sources:
 *   - /api/portfolios/<id>/overview?asset_id=...&period=...   (real)
 *       generation_mwh, curtailment split + series, capture history, battery
 *       revenue stack (FCAS components)
 *   - /api/batteries/<duid>/commercial?days=...               (real, BESS only)
 *       FCAS revenue stack
 *
 * Fields not yet served by the backend (availability %, RTE %, equivalent
 * cycles, SoH, dispatch compliance, constraint hours, etc.) are generated
 * deterministically from an asset+period hash so the page reads like real
 * ops data without jumping on re-render. Each stub is annotated `// STUB:`
 * so the backend wire-up is greppable.
 */

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box, Typography, Card, Stack, Skeleton, Alert, IconButton,
  LinearProgress, TextField, InputAdornment, Tooltip, Select, MenuItem,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import { BarChart } from '@mui/x-charts/BarChart';
import { LineChart } from '@mui/x-charts/LineChart';

import KpiCard, {
  fmtMWhCompact, fmtPct,
} from './KpiCard';
import { useThemeColors } from '../theme/ThemeModeContext';
import PeriodPicker, { usePersistedPeriod } from './PeriodPicker';
import AssetPicker from './AssetPicker';
import { cachedGet } from '../utils/cachedFetch';
import { periodApiParams } from '../utils/periodParams';
// ENERGY_PALETTE is the canonical curtailment-cause colour map; the
// WaterfallChart default export drives the Curtailment-waterfall card next
// to the KPI tiles. (The Constraint Pareto moved to the Curtailment tab —
// see ConstraintParetoCard.js.)
import WaterfallChart, { ENERGY_PALETTE } from './WaterfallChart';
import TechnicalWorkspaceTabs from './TechnicalWorkspaceTabs';

const API_BASE_URL = process.env.REACT_APP_API_URL ?? 'http://localhost:5001';

// LTM capacity-factor trend — hidden after the 2026-06-10 METIS review (not
// in the client requirements; the LTM overlay is also still a stub). Flip to
// true to restore the card in the generation trend row.
const SHOW_LTM_CF_TREND = false;

// ── Palettes ────────────────────────────────────────────────────────────────

// Loss waterfall colours now live in ENERGY_PALETTE inside WaterfallChart.js
// — keeping the shared palette in one place. Use that import everywhere.

// FCAS service palette (same hues used on Commercial Performance).
const FCAS_COLOR = {
  RAISEREG:   '#E9B065',
  LOWERREG:   '#3b82f6',
  RAISE6SEC:  '#a78bfa',
  LOWER6SEC:  '#60a5fa',
  RAISE60SEC: '#b39bff',
  LOWER60SEC: '#7dd3fc',
  RAISE5MIN:  '#c4b5fd',
  LOWER5MIN:  '#93c5fd',
  RAISE1SEC:  '#8b5cf6',
  LOWER1SEC:  '#0ea5e9',
};

// ── Helpers ─────────────────────────────────────────────────────────────────

const fmtDate = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' });
};

const fmtHrs = (h) => {
  if (h == null || isNaN(h)) return '—';
  if (h < 1) return `${Math.round(h * 60)} min`;
  if (h < 24) return `${h.toFixed(1)} h`;
  return `${(h / 24).toFixed(1)} d`;
};

const fmtInt = (n) => (n == null || isNaN(n) ? '—' : Math.round(n).toLocaleString());

// Deterministic 0..1 hash so STUBs read the same across re-renders for the
// same (asset, period) pair. Cheap FNV-like rolling hash — good enough to
// stop values jumping; not security-grade.
function seed01(...parts) {
  const s = parts.join('|');
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  // Map to [0,1).
  return ((h >>> 0) % 1_000_000) / 1_000_000;
}

// STUB helper — pick a value in [lo, hi] deterministically per asset+period+key.
const stubBetween = (assetId, period, key, lo, hi) => {
  const t = seed01(assetId || '_', period?.code || '_', period?.start || '_', key);
  return lo + (hi - lo) * t;
};

// Build a deterministic daily series with a base + noise band, optionally
// trending up/down. Returns an array of {day:Date, value:number}.
function stubSeries(assetId, period, key, days, base, jitter, trend = 0) {
  const start = period?.start ? new Date(period.start) : new Date();
  const out = [];
  for (let i = 0; i < days; i++) {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    const r = seed01(assetId || '_', period?.code || '_', key, String(i));
    const drift = trend * (i / Math.max(days - 1, 1));
    out.push({ day: d, value: base + drift + (r - 0.5) * 2 * jitter });
  }
  return out;
}

// How many days are in the selected period (clamp for stub-series sizing).
function periodDays(period) {
  if (!period?.start || !period?.end) return 30;
  const ms = new Date(period.end).getTime() - new Date(period.start).getTime();
  return Math.max(1, Math.min(180, Math.round(ms / 86400000)));
}

// Map AEMO curtailment cause label → KPI-bucket.
function sumCause(curtailment, cause) {
  const row = (curtailment?.split || []).find((s) => s.cause === cause);
  return row ? row.mwh : 0;
}

// ── Main component ──────────────────────────────────────────────────────────

const PortfolioPerformance = () => {
  const { portfolioId } = useParams();
  const colors = useThemeColors();

  // Period + asset state. Mirrors Commercial Performance so the two pages
  // share controls and muscle memory carries over.
  // Shared across every Technical tab (sessionStorage) — the window picked
  // here follows the user to Curtailment / Outages / Forecast accuracy.
  const [period, setPeriod]   = usePersistedPeriod('lumeros.technical.period', '30d');
  const [assetId, setAssetId] = useState(null);
  const [assets, setAssets]   = useState([]);

  const [data, setData]             = useState(null);
  const [batteryData, setBatteryData] = useState(null);
  const [dispatchData, setDispatchData] = useState(null);
  // New `/technical` endpoint payload — composes the 7 atlas helpers
  // (dispatch_compliance, forced_outage_events, capacity_factor_daily,
  // setpoint_compliance, setpoint_deviation_histogram, bess_cycles_rte_daily,
  // bess_soc_hour_of_day, fcas_enablement). Keyed by `sections.<name>.data`.
  const [techData, setTechData]     = useState(null);
  const [loading, setLoading]       = useState(true);
  const [batteryLoading, setBatteryLoading] = useState(false);
  const [dispatchLoading, setDispatchLoading] = useState(false);
  const [techLoading, setTechLoading] = useState(false);
  const [error, setError]           = useState(null);

  const selectedAsset = useMemo(
    () => (assets || []).find((a) => a.asset_id === assetId),
    [assets, assetId],
  );
  const isBattery = selectedAsset?.fuel === 'Battery';
  const capacityMw = selectedAsset?.capacity_mw || selectedAsset?.capacity || 0;

  // Bootstrap the asset roster on mount. We hit /overview for the canonical
  // list (same as Commercial Performance) so the picker matches both pages.
  useEffect(() => {
    let cancelled = false;
    cachedGet(`${API_BASE_URL}/api/portfolios/${portfolioId}/overview`,
              { params: { period: '30d' } })
      .then((r) => {
        if (cancelled) return;
        const list = r.data?.assets || [];
        setAssets(list);
        if (list.length > 0 && !assetId) setAssetId(list[0].asset_id);
      })
      .catch(() => { if (!cancelled) setAssets([]); });
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [portfolioId]);

  // Main fetch — /overview already serves the technical-relevant slice
  // (generation, curtailment, capture history). For dedicated technical
  // metrics we'll add a /technical endpoint later; today we layer STUB
  // values on top of the real data so the UI is complete.
  const load = useCallback(async (periodValue, scope) => {
    if (!scope) return;
    setLoading(true); setError(null);
    try {
      // Explicit start/end for every preset — the picker is the single
      // source of truth for the window (see utils/periodParams.js).
      const params = periodApiParams(periodValue);
      if (scope && scope !== 'all') params.asset_id = scope;
      const r = await cachedGet(
        `${API_BASE_URL}/api/portfolios/${portfolioId}/overview`,
        { params });
      setData(r.data);
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setLoading(false);
    }
  }, [portfolioId]);

  useEffect(() => { load(period, assetId); }, [load, period, assetId]);

  // (The prior-period /overview + /technical fetches that fed the KPI
  // "vs prior" Δ chips were removed 2026-06-11 — the chips confused more
  // than they informed, and dropping them saves two BQ round-trips per
  // page view.)

  // Battery overlay fetch — only when the active asset is a BESS. Uses the
  // existing /api/batteries/<duid>/commercial endpoint to grab the FCAS
  // revenue stack (needed for the FCAS enablement breakdown). Same path
  // Commercial Performance uses.
  useEffect(() => {
    if (!isBattery || !assetId) { setBatteryData(null); return; }
    setBatteryLoading(true);
    const days = periodDays(period);
    let cancelled = false;
    cachedGet(`${API_BASE_URL}/api/batteries/${assetId}/commercial`,
              { params: { days } })
      .then((r) => { if (!cancelled) setBatteryData(r.data); })
      .catch(() => { if (!cancelled) setBatteryData(null); })
      .finally(() => { if (!cancelled) setBatteryLoading(false); });
    return () => { cancelled = true; };
  }, [isBattery, assetId, period]);

  // Technical metrics fetch — single round-trip to the new /technical
  // endpoint which composes the modular atlas helpers (compliance, outages,
  // CF daily, plus battery-only cycles/RTE/SoC/FCAS). Returns the same
  // shape for both flavours; battery-only sections are absent on non-BESS
  // DUIDs (the route layer skips those atlas calls).
  useEffect(() => {
    if (!assetId) { setTechData(null); return; }
    setTechLoading(true);
    // Forward `frequency` so the FCAS enablement chart (the one bucketed
    // helper on /technical) honours the picker's 5min / 30min / hourly /
    // daily / weekly / monthly / yearly setting. Everything else on the
    // endpoint is either a period total or a per-day rollup and ignores it.
    const params = { ...periodApiParams(period), asset_id: assetId };
    let cancelled = false;
    cachedGet(`${API_BASE_URL}/api/portfolios/${portfolioId}/technical`, { params })
      .then((r) => { if (!cancelled) setTechData(r.data); })
      .catch(() => { if (!cancelled) setTechData(null); })
      .finally(() => { if (!cancelled) setTechLoading(false); });
    return () => { cancelled = true; };
  }, [portfolioId, assetId, period]);

  // Per-asset dispatch series — drives the hour-of-day charge/discharge
  // profile (battery flavour only; the generation hour-of-day curve was
  // killed in the 2026-06-10 METIS review — a solar profile is always the
  // same shape, so it earned no space). /api/portfolios/<id>/dispatch
  // already returns the bucketed MW series for a single DUID.
  useEffect(() => {
    if (!assetId || !isBattery) { setDispatchData(null); return; }
    setDispatchLoading(true);
    const params = periodApiParams(period, { withFrequency: false });
    // Force hourly bucketing — hour-of-day rollups don't need 5-min
    // resolution and a 5-min response for 30 days is ~8000 points.
    params.frequency = 'hourly';
    params.asset_id = assetId;
    let cancelled = false;
    cachedGet(`${API_BASE_URL}/api/portfolios/${portfolioId}/dispatch`, { params })
      .then((r) => { if (!cancelled) setDispatchData(r.data); })
      .catch(() => { if (!cancelled) setDispatchData(null); })
      .finally(() => { if (!cancelled) setDispatchLoading(false); });
    return () => { cancelled = true; };
  }, [portfolioId, assetId, isBattery, period]);

  // ── Derive real metrics from /overview where we can ─────────────────────

  // Backend returns `kpis` (plural) — we were previously reading `kpi`
  // (singular), which silently fell back to {} and made `generationMwh`
  // resolve to 0 everywhere on this page. Accept both shapes defensively
  // so a future renaming doesn't blow up but treat `kpis` as canonical.
  const k  = data?.kpis      || data?.kpi      || {};
  const generationMwh = k.generation_mwh || 0;
  const curtailment = data?.curtailment || { curtailed_mwh: 0, split: [] };
  const econCurtailMwh = sumCause(curtailment, 'Neg. price');
  const gridCurtailMwh = sumCause(curtailment, 'Network');
  const thermalCurtailMwh = sumCause(curtailment, 'Thermal limit');
  const totalCurtailMwh = econCurtailMwh + gridCurtailMwh + thermalCurtailMwh;
  const totalPotentialMwh = generationMwh + totalCurtailMwh;
  const curtailmentPct = totalPotentialMwh > 0
    ? (totalCurtailMwh / totalPotentialMwh) * 100
    : 0;

  // Capacity factor = MWh delivered / (capacity × hours_in_period).
  const hoursInPeriod = periodDays(period) * 24;
  const theoreticalMwh = capacityMw * hoursInPeriod;
  const capacityFactorPct = theoreticalMwh > 0
    ? (generationMwh / theoreticalMwh) * 100
    : 0;

  // ── Technical metrics — real data where available, stubs flagged ───────

  // Availability % — REAL. atlas.asset_status_snapshot() already computes
  // AVG(AVAILABILITY) / REGISTEREDCAPACITY × 100 per DUID and returns it on
  // /overview as data.kpis.availability_pct (portfolio-level) plus
  // data.assets[].availability_pct (per asset). When a single asset is
  // selected we prefer the per-asset value so the KPI matches the picker.
  const perAssetAvailability = (data?.assets || [])
    .find((a) => a.asset_id === assetId)?.availability_pct;
  const availabilityPct = perAssetAvailability
    ?? data?.kpis?.availability_pct
    ?? (90 + stubBetween(assetId, period, 'avail', 0, 9));  // ultimate fallback

  // Convenience reader for the /technical payload — every section is
  // wrapped in `{data, error}` so the route layer can fail one card without
  // breaking the page. `techSection('dispatch_compliance')` → the data block
  // or null if missing/errored.
  const techSection = (key) => techData?.sections?.[key]?.data ?? null;

  // Forced outages — REAL via atlas.forced_outage_events (heuristic on
  // AVAILABILITY step-drops below 20% of capacity, with hours = intervals
  // below 50% of capacity).
  const _fo = techSection('forced_outages');
  const forcedOutageCount = _fo?.events ?? 0;
  const forcedOutageHrs   = _fo?.hours  ?? 0;

  // Dispatch compliance — REAL via atlas.dispatch_compliance with a 5%
  // threshold (AEMO's "compliant follow" rule of thumb).
  const _dc = techSection('dispatch_compliance');
  const dispatchCompliancePct      = _dc?.compliance_pct ?? null;

  // Constraint-bound hours — REAL via atlas.constraint_binding (joins
  // DISPATCHCONSTRAINT × GENCONDATA). Returns total bound hours plus the
  // Pareto-ordered list of binding constraint codes for this DUID.
  const _cb = techSection('constraint_binding');
  const constraintHrs = _cb?.bound_hours != null
    ? Math.round(_cb.bound_hours)
    : 0;

  // Battery metrics — REAL via /api/batteries/<duid>/commercial.
  // atlas.bess_revenue_stack() already computes:
  //   - rte_pct       = discharge_mwh / charge_mwh × 100
  //   - full_cycles   = (charge + discharge) / 2 / MAXSTORAGECAPACITY
  //   - avg_soc_pct   = AVG(INITIAL_ENERGY_STORAGE) / MAXSTORAGECAPACITY × 100
  const battKpi = batteryData?.kpis || {};
  const rtePct = battKpi.rte_pct
    ?? (84 + stubBetween(assetId, period, 'rte', 0, 6));    // fallback only
  const designRtePct = 88;   // industry default; could come from contract metadata later

  const equivalentCycles = battKpi.full_cycles
    ?? stubBetween(assetId, period, 'cyc', 80, 220);        // fallback only
  const warrantyAnnualCycles = 730;   // industry default: 2 cycles/day × 365
  const cyclesBudgetPct = (equivalentCycles / (warrantyAnnualCycles * periodDays(period) / 365)) * 100;

  // SoC utilisation — currently uses avg SoC as a proxy for "where in the
  // range we sit". True utilisation = (max - min) / usable_range_pct over
  // the period, which needs a min/max rollup on the BQ side — not on
  // /commercial today. Falling back to avg for now.
  const socUtilisationPct = battKpi.avg_soc_pct
    ?? (80 + stubBetween(assetId, period, 'soc_util', 0, 18));
  // SoH is hidden in the UI until a BMS feed is wired — kept here so the
  // prop pipeline stays intact for a one-line un-hide.
  // eslint-disable-next-line no-unused-vars
  const sohPct = 96 + stubBetween(assetId, period, 'soh', 0, 3);    // STUB: implied from RTE drift trend

  // Setpoint compliance — REAL via atlas.dispatch_compliance with the
  // tighter 2% threshold the BESS control-loop spec asks for. Returned by
  // /technical as a separate section so generation + battery KPIs read
  // from different thresholds without re-querying.
  const setpointCompliancePct = techSection('setpoint_compliance')?.compliance_pct
    ?? (95 + stubBetween(assetId, period, 'sp', 0, 4.5));    // fallback

  // FCAS enablement % — REAL via atlas.fcas_enablement (any-service interval
  // count, not revenue-weighted). When the new endpoint hasn't responded yet
  // we briefly fall back to a revenue-presence estimate.
  const fcasComponents = batteryData?.revenue_stack?.components || [];
  const fcasEnablementPct = techSection('fcas_enablement')?.any_service_pct
    ?? (fcasComponents.length > 0
        ? Math.min(99.5, 60 + stubBetween(assetId, period, 'fcas_en', 0, 35))
        : (80 + stubBetween(assetId, period, 'fcas_en', 0, 18)));

  // ── Render ─────────────────────────────────────────────────────────────

  return (
    <Box sx={{ p: { xs: 1.5, sm: 2, md: 2.5 } }}>
      {/* Tab strip — Performance / Curtailment / Outages / Forecast accuracy /
          Degradation / Peer benchmark / Benchmarks. Without this the sidebar's
          "Technical Performance" entry lands here with no way to navigate to
          any of the sibling sub-views. */}
      <TechnicalWorkspaceTabs />
      {/* Header — title left, asset + period + refresh right. Same chrome
          as Commercial Performance and Facility & Covenants. */}
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
        <Typography sx={{
          fontSize: '1.5rem', fontWeight: 700, letterSpacing: '-0.025em',
          color: 'text.primary',
        }}>
          Technical Performance
        </Typography>
        <Stack direction="row" alignItems="center" spacing={1}>
          {assets.length > 1 && (
            <AssetPicker
              value={assetId || ''}
              onChange={setAssetId}
              assets={assets}
              colors={colors}
              allowAll={false}
              minWidth={220}
              placeholder="Select asset"
            />
          )}
          <PeriodPicker value={period} onChange={setPeriod} size="small" />
          <IconButton
            size="small"
            onClick={() => load(period, assetId)}
            disabled={loading}
            sx={{ color: 'text.secondary' }}
            title="Refresh"
          >
            <RefreshIcon fontSize="small" />
          </IconButton>
        </Stack>
      </Stack>

      {/* Sub-header chip — period · range · active asset */}
      {period && (
        <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2.5, ml: 0.25 }}>
          <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75,
                     color: 'text.secondary', fontSize: '0.8rem' }}>
            <FiberManualRecordIcon sx={{ fontSize: 8, color: 'primary.main' }} />
            <Box component="span" sx={{ fontWeight: 600, color: 'text.primary' }}>
              {period.label}
            </Box>
            <Box component="span" sx={{ color: 'text.disabled' }}>·</Box>
            <Box component="span" sx={{ fontVariantNumeric: 'tabular-nums' }}>
              {fmtDate(period.start)}
              <Box component="span" sx={{ mx: 0.5, color: 'text.disabled' }}>→</Box>
              {fmtDate(period.end)}
            </Box>
            {assetId && (
              <>
                <Box component="span" sx={{ color: 'text.disabled' }}>·</Box>
                <Box component="span" sx={{ color: 'primary.main', fontWeight: 600 }}>
                  {selectedAsset?.name || assetId}
                </Box>
                {selectedAsset?.fuel && (
                  <Box component="span" sx={{
                    ml: 0.5, px: 0.75, py: 0.1, borderRadius: '4px',
                    fontSize: '0.7rem', fontWeight: 700, letterSpacing: '0.04em',
                    bgcolor: isBattery ? '#8b5cf61f' : '#22c55e1f',
                    color: isBattery ? '#8b5cf6' : '#22c55e',
                  }}>
                    {selectedAsset.fuel.toUpperCase()}
                  </Box>
                )}
              </>
            )}
          </Box>
        </Stack>
      )}

      {error && <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>{error}</Alert>}

      {/* ── KPI section ─────────────────────────────────────────────────────
          Battery flavour stays as a 3×2 full-width KPI grid because the
          main row below already has SoC heatmap + Battery Health card.
          Generation flavour pairs a 2×3 KPI grid (40%) with the
          curtailment waterfall on the right (60%) — the taller tile
          column gives the waterfall the height it needs to read. */}
      <Box sx={{
        display: 'grid',
        gridTemplateColumns: isBattery
          ? { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' }
          : { xs: '1fr', lg: '4fr 6fr' },
        gap: 2,
        mb: 3,
      }}>
        {isBattery ? (
          <>
            <KpiCard
              label="Round-trip efficiency"
              value={fmtPct(rtePct)}
              accent="#22c55e"
              tooltip={`Energy out / energy in. Design RTE: ${designRtePct}%.`}
              loading={loading}
            />
            <KpiCard
              label="Equivalent full cycles"
              value={fmtInt(equivalentCycles)}
              unit={`cycles · ${cyclesBudgetPct.toFixed(0)}% of pro-rata budget`}
              accent="#8b5cf6"
              tooltip="Sum of charge throughput ÷ usable energy capacity. Compared against the warranty-budgeted 2 cycles/day default."
              loading={loading}
            />
            <KpiCard
              label="SoC utilisation"
              value={fmtPct(socUtilisationPct)}
              accent="#06b6d4"
              tooltip="% of usable SoC range cycled through in this window."
              loading={loading}
            />
            <KpiCard
              label="FCAS enablement"
              value={fmtPct(fcasEnablementPct)}
              unit="of intervals"
              accent="#E9B065"
              tooltip="% of dispatch intervals with at least one FCAS service enabled."
              loading={loading}
            />
            <KpiCard
              label="Availability"
              value={fmtPct(availabilityPct)}
              accent="#3b82f6"
              tooltip="% of intervals where AVAILABILITY > 0 (DUID was offerable)."
              loading={loading}
            />
            <KpiCard
              label="Setpoint compliance"
              value={fmtPct(setpointCompliancePct)}
              unit="vs TOTALCLEARED"
              accent="#f59e0b"
              tooltip="% of intervals where |actual − target| < 2% of capacity. AGC follow quality."
              loading={loading}
            />
          </>
        ) : (
          // Generation flavour — 6 KPI tiles as a 2-col × 3-row grid in the
          // left 40%, with the curtailment waterfall in the right 60%.
          <>
            <Box sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' },
              gap: 2,
            }}>
            <KpiCard
              label="Availability"
              value={fmtPct(availabilityPct)}
              accent="#22c55e"
              tooltip="% of intervals where AVAILABILITY ≥ 95% of capacity."
              loading={loading}
            />
            <KpiCard
              label="Capacity factor"
              value={fmtPct(capacityFactorPct)}
              accent="#3b82f6"
              tooltip={`Generation MWh ÷ (${capacityMw} MW × ${hoursInPeriod} h).`}
              loading={loading}
            />
            <KpiCard
              label="Curtailment"
              value={fmtPct(curtailmentPct)}
              unit={`${fmtMWhCompact(totalCurtailMwh)} lost`}
              accent="#ef4444"
              tooltip="Energy the asset could have produced but was dispatched down — as a share of its potential. Economic = the unit's own bid priced it out of the market; grid = a network or system constraint held it back."
              loading={loading}
            />
            <KpiCard
              label="Forced outages"
              value={fmtInt(forcedOutageCount)}
              unit={`events · ${fmtHrs(forcedOutageHrs)}`}
              accent="#dc2626"
              tooltip="Unplanned drops in AVAILABILITY without prior ramp-down (heuristic; needs SCADA event tagging)."
              loading={loading}
            />
            <KpiCard
              label="Dispatch compliance"
              value={fmtPct(dispatchCompliancePct)}
              accent="#06b6d4"
              tooltip="% of intervals where |actual − TOTALCLEARED| < 5% of capacity."
              loading={loading}
            />
            <KpiCard
              label="Constraint-bound hours"
              value={fmtInt(constraintHrs)}
              unit="h"
              accent="#f59e0b"
              tooltip="Hours the asset was on a binding NEM constraint with non-zero marginal value."
              loading={loading}
            />
            </Box>
            <CurtailmentWaterfallCard
              deliveredMwh={generationMwh}
              econCurtailMwh={econCurtailMwh}
              gridCurtailMwh={gridCurtailMwh + thermalCurtailMwh}
              loading={loading}
              colors={colors}
            />
          </>
        )}
      </Box>

      {/* ── Main row (battery only) ────────────────────────────────────────
          Battery: SoC heatmap (left 55%) + Battery Health card (right 45%).
          Generation has no main row since the 2026-06-11 trim: the
          Generation & curtailment timeline and the Asset Health card were
          dropped — the KPI tiles + curtailment waterfall already carry the
          headline numbers, and the Curtailment tab has the per-bucket
          detail. */}
      {isBattery && (
        <Box sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', lg: '11fr 9fr' },
          gap: 2,
          mb: 3,
        }}>
          <SocProfileCard
            socSeries={techSection('soc_hour_of_day')}
            assetId={assetId}
            period={period}
            loading={techLoading || batteryLoading || loading}
            colors={colors}
          />
          <BatteryHealthCard
            cyclesUsed={equivalentCycles}
            cyclesBudgetPct={cyclesBudgetPct}
            warrantyAnnual={warrantyAnnualCycles}
            sohPct={sohPct}
            rtePct={rtePct}
            designRtePct={designRtePct}
            fcasComponents={fcasComponents}
            loading={loading || batteryLoading}
          />
        </Box>
      )}

      {/* ── Trend section ─────────────────────────────────────────────────
          Battery flavour stays as a 3-up (cycles · RTE · FCAS).
          Generation flavour: just a 2-up of CF trend + dispatch
          compliance — the Generation & curtailment timeline moved up into
          the main row (75/25 with Asset Health) for a more compact
          page-level reading flow. */}
      {isBattery ? (
        <Box sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
          gap: 2,
          mb: 3,
        }}>
          <CycleLedgerCard
            cyclesSeries={techSection('cycles_rte_daily')}
            assetId={assetId}
            period={period}
            warrantyAnnual={warrantyAnnualCycles}
            loading={techLoading || loading}
            colors={colors}
          />
          <RteTrendCard
            cyclesSeries={techSection('cycles_rte_daily')}
            assetId={assetId}
            period={period}
            designRte={designRtePct}
            loading={techLoading || loading}
            colors={colors}
          />
          <FcasEnablementCard
            fcasSeries={techSection('fcas_enablement_series')}
            fcasEnablement={techSection('fcas_enablement')}
            fcasComponents={fcasComponents}
            assetId={assetId}
            period={period}
            loading={techLoading || batteryLoading || loading}
            colors={colors}
          />
        </Box>
      ) : (
        // Generation trend section — the headline Availability timeline
        // moved up into the main row alongside Asset Health (75/25). The
        // LTM CF trend is flag-hidden (METIS review), so by default this
        // row is Dispatch compliance full-width until it moves to PPA.
        <>
          <Box sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              md: SHOW_LTM_CF_TREND ? 'repeat(2, 1fr)' : '1fr',
            },
            gap: 2,
            mb: 3,
          }}>
            {SHOW_LTM_CF_TREND && (
              <CapacityFactorTrendCard
                cfSeries={techSection('capacity_factor_daily')}
                assetId={assetId}
                period={period}
                currentCfPct={capacityFactorPct}
                loading={techLoading || loading}
                colors={colors}
              />
            )}
            <DispatchComplianceCard
              dailySeries={techSection('dispatch_compliance_daily')}
              assetId={assetId}
              period={period}
              currentPct={dispatchCompliancePct}
              loading={techLoading || loading}
              colors={colors}
            />
          </Box>
        </>
      )}

      {/* ── Production by threshold (generation only) ────────────────────
          Moved in from the Spot Price tab per the 2026-06-10 METIS review —
          "this is a production thing", so it lives with the production
          story. Splits generated MWh by what the spot RRP was when it
          dispatched (RRP > 0 / LGC-only / uneconomic). */}
      {!isBattery && (
        <Box sx={{ mb: 3 }}>
          <ProductionByThresholdCard
            portfolioId={portfolioId}
            assetId={assetId}
            period={period}
            colors={colors}
          />
        </Box>
      )}

      {/* ── Deep-dive row (battery only) ──────────────────────────────────
          Hour-of-day charge/discharge profile + setpoint deviation
          histogram (how tightly does the BESS follow AGC?). The generation
          curtailment-vs-spot-price scatter was dropped in the 2026-06-11
          trim — the full-size version lives on the Curtailment tab. */}
      {isBattery && (
        <Box sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' },
          gap: 2,
          mb: 3,
        }}>
          <HourOfDayBatteryCard
            dispatchData={dispatchData}
            assetId={assetId}
            period={period}
            capacityMw={capacityMw}
            loading={dispatchLoading || loading}
            colors={colors}
          />
          <SetpointDeviationCard
            histogram={techSection('setpoint_deviation_histogram')}
            assetId={assetId}
            period={period}
            setpointCompliancePct={setpointCompliancePct}
            loading={techLoading || loading}
            colors={colors}
          />
        </Box>
      )}
    </Box>
  );
};

export default PortfolioPerformance;


// ════════════════════════════════════════════════════════════════════════════
// Sub-components — kept in the same file to mirror CommercialOverview's shape.
// Each one is small enough that the boundary cost wouldn't pay back.
// ════════════════════════════════════════════════════════════════════════════

// ── CurtailmentWaterfallCard ────────────────────────────────────────────────
// Potential → −economic → −grid → Delivered, each step labelled as % of
// potential. Replaced the loss-split donut: the donut showed how losses
// split between causes but not the headline "what share of potential did we
// lose" — the waterfall reads both at a glance, and it's the same shape the
// Curtailment tab told this story with. Canonical WaterfallChart +
// ENERGY_PALETTE so every screen colours the causes identically.
//
// Data inputs: real BQ values from /overview (data.kpis.generation_mwh +
// data.curtailment.split — both bid-engine numbers).
function CurtailmentWaterfallCard({
  deliveredMwh, econCurtailMwh, gridCurtailMwh, loading, colors,
}) {
  const steps = useMemo(() => {
    const econ = Math.max(0, econCurtailMwh || 0);
    const grid = Math.max(0, gridCurtailMwh || 0);
    const delivered = Math.max(0, deliveredMwh || 0);
    const potential = delivered + econ + grid;
    if (potential <= 0) return [];
    const pct = (v) => `${((v / potential) * 100).toFixed(1)}%`;
    // Axis carries the clean cause names (same wording as the Curtailment
    // tab); each bar's % of potential renders under its value via subLabel,
    // not in the x-axis label.
    return [
      { label: 'Potential', kind: 'start',
        value: potential, color: ENERGY_PALETTE.potential },
      { label: 'Economic Curtailment', subLabel: pct(econ), kind: 'loss',
        value: econ, color: ENERGY_PALETTE.econ_curt },
      { label: 'Grid Curtailment', subLabel: pct(grid), kind: 'loss',
        value: grid, color: ENERGY_PALETTE.grid_curt },
      { label: 'Delivered', subLabel: pct(delivered), kind: 'end',
        value: delivered, color: ENERGY_PALETTE.delivered },
    ];
  }, [deliveredMwh, econCurtailMwh, gridCurtailMwh]);

  return (
    // Sized to match the 2×3 KPI-tile grid it sits beside — the row height
    // follows the tallest item, and an oversized chart here stretches every
    // KPI tile with dead space at the bottom.
    <Card sx={{ p: 2.5, display: 'flex', flexDirection: 'column' }}>
      <Stack direction="row" justifyContent="space-between" alignItems="baseline" sx={{ mb: 0.5 }}>
        <Typography variant="overline" sx={{ color: 'text.secondary' }}>
          Curtailment waterfall
        </Typography>
        <Typography variant="caption" sx={{ color: 'text.disabled' }}>
          % of potential
        </Typography>
      </Stack>
      {loading ? (
        <Skeleton variant="rectangular" height={260} sx={{ bgcolor: colors.skeleton, borderRadius: 1 }} />
      ) : (
        <WaterfallChart
          steps={steps}
          height={260}
          formatValue={fmtMWhCompact}
          colors={colors}
          seriesLabels={{ start: 'Potential', loss: 'Curtailed', end: 'Delivered' }}
          emptyMessage="No generation data in this period."
        />
      )}
    </Card>
  );
}


// Small label/value/sub stat block — used by BatteryHealthCard.
function StatLine({ label, value, sub, loading }) {
  return (
    <Box>
      <Typography variant="caption" sx={{ color: 'text.disabled', letterSpacing: '0.08em', textTransform: 'uppercase' }}>
        {label}
      </Typography>
      {loading ? (
        <Skeleton variant="text" width="70%" height={26} />
      ) : (
        <>
          <Typography sx={{
            fontSize: '1.05rem', fontWeight: 600, lineHeight: 1.15,
            fontVariantNumeric: 'tabular-nums', color: 'text.primary', mt: 0.25,
          }}>
            {value}
          </Typography>
          {sub && (
            <Typography sx={{ fontSize: '0.7rem', color: 'text.disabled', mt: 0.15 }}>
              {sub}
            </Typography>
          )}
        </>
      )}
    </Box>
  );
}


// ── BatteryHealthCard ──────────────────────────────────────────────────────
// sohPct is wired through but hidden until BMS data lands — the
// eslint-disable below silences the "unused" warning for the destructured
// prop. Re-enabling SoH is a single-line uncomment in the JSX below.
function BatteryHealthCard({
  cyclesUsed, cyclesBudgetPct, warrantyAnnual,
  // eslint-disable-next-line no-unused-vars
  sohPct, rtePct, designRtePct, fcasComponents, loading,
}) {
  // FCAS service uptime — service-by-service. Real source would be the %
  // of intervals each market was enabled; STUBbed below from the revenue
  // stack composition so heavy-bid markets show high uptime.
  const fcasServices = useMemo(() => {
    if (!fcasComponents?.length) return [];
    const totalAud = fcasComponents.reduce((a, c) => a + (c.aud || 0), 0) || 1;
    // Pick services with > 0 revenue; cap uptime to a believable 99.5%.
    return fcasComponents
      .filter((c) => (c.aud || 0) > 0 && c.key?.match(/^(RAISE|LOWER)/))
      .map((c) => ({
        key: c.key,
        label: c.label || c.key,
        uptimePct: Math.min(99.5, 30 + ((c.aud || 0) / totalAud) * 350),
      }))
      .sort((a, b) => b.uptimePct - a.uptimePct)
      .slice(0, 6);
  }, [fcasComponents]);

  return (
    <Card sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
      <Typography variant="overline" sx={{ color: 'text.secondary' }}>
        Battery health
      </Typography>

      {/* Cycles consumed vs warranty budget */}
      <Box>
        <Stack direction="row" alignItems="baseline" justifyContent="space-between">
          <Typography variant="caption" sx={{ color: 'text.disabled', letterSpacing: '0.08em', textTransform: 'uppercase' }}>
            Cycles consumed
          </Typography>
          <Typography sx={{ fontSize: '0.78rem', color: 'text.secondary', fontVariantNumeric: 'tabular-nums' }}>
            {fmtInt(cyclesUsed)} / {warrantyAnnual} pa
          </Typography>
        </Stack>
        {loading ? (
          <Skeleton variant="rectangular" height={8} sx={{ mt: 1, borderRadius: 4 }} />
        ) : (
          <Box sx={{ mt: 1 }}>
            <LinearProgress
              variant="determinate"
              value={Math.min(100, cyclesBudgetPct)}
              sx={{
                height: 8, borderRadius: 4,
                bgcolor: 'rgba(139, 92, 246, 0.15)',
                '& .MuiLinearProgress-bar': {
                  bgcolor: cyclesBudgetPct > 110 ? '#ef4444'
                    : cyclesBudgetPct > 90  ? '#f59e0b'
                    : '#8b5cf6',
                },
              }}
            />
            <Typography sx={{ fontSize: '0.7rem', color: 'text.disabled', mt: 0.5 }}>
              {cyclesBudgetPct.toFixed(0)}% of pro-rata budget
            </Typography>
          </Box>
        )}
      </Box>

      {/* RTE vs design. State of Health is hidden until BMS data is wired —
          there's no AEMO-side proxy for cell-level health, only the RTE
          drift inference, which we don't want to publish as a number yet. */}
      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr', gap: 1.5 }}>
        <StatLine
          label="RTE vs design"
          value={`${fmtPct(rtePct)} / ${designRtePct}%`}
          sub={rtePct < designRtePct - 2 ? 'below design' : 'on track'}
          loading={loading}
        />
        {/* HIDDEN UNTIL BMS — State of Health.
            <StatLine
              label="State of Health"
              value={fmtPct(sohPct)}
              sub="implied from RTE drift"
              loading={loading}
            /> */}
      </Box>

      {/* FCAS service-by-service uptime */}
      <Box>
        <Typography variant="caption" sx={{ color: 'text.disabled', letterSpacing: '0.08em', textTransform: 'uppercase' }}>
          FCAS service uptime
        </Typography>
        <Box sx={{ mt: 0.75, display: 'flex', flexDirection: 'column', gap: 0.6 }}>
          {loading
            ? [0, 1, 2, 3].map((i) => <Skeleton key={i} height={14} />)
            : fcasServices.length === 0
              ? <Typography sx={{ fontSize: '0.78rem', color: 'text.disabled' }}>
                  No FCAS enablement in this window.
                </Typography>
              : fcasServices.map((s) => (
                  <Stack key={s.key} direction="row" alignItems="center" spacing={1}>
                    <Box sx={{ width: 70, fontSize: '0.72rem', fontFamily: 'monospace',
                               color: 'text.secondary' }}>
                      {s.key}
                    </Box>
                    <Box sx={{ flexGrow: 1, height: 6, borderRadius: 3,
                               bgcolor: 'rgba(255,255,255,0.06)', overflow: 'hidden' }}>
                      <Box sx={{
                        height: '100%', width: `${s.uptimePct}%`,
                        bgcolor: FCAS_COLOR[s.key] || '#a78bfa',
                        transition: 'width 240ms ease',
                      }} />
                    </Box>
                    <Box sx={{ width: 38, textAlign: 'right',
                               fontSize: '0.72rem', fontVariantNumeric: 'tabular-nums',
                               color: 'text.primary' }}>
                      {s.uptimePct.toFixed(0)}%
                    </Box>
                  </Stack>
                ))}
        </Box>
      </Box>
    </Card>
  );
}


// ── SocProfileCard ─────────────────────────────────────────────────────────
// Day × hour heatmap of average state-of-charge. Standard "battery clock" view
// — at a glance the user can tell when the battery charges and discharges.
// STUB: real source is per-interval SoC from the BESS BMS; here we synthesise
// a plausible pattern (charge overnight 22–06, discharge evening 17–21).
function SocProfileCard({ socSeries, assetId, period, loading, colors }) {
  const days = periodDays(period);

  // Real BMS data: atlas.bess_soc_hour_of_day returns one row per
  // (day, hour-of-day) with the avg SoC %. We pivot that into the
  // {day, hour, soc} cell shape the grid renders. Falls back to a
  // synthetic charge-overnight / discharge-evening pattern when the
  // /technical request is still in flight or the asset isn't a battery
  // with INITIAL_ENERGY_STORAGE.
  const cells = useMemo(() => {
    if (socSeries && socSeries.length) {
      return socSeries.map((r) => ({
        day:  new Date(r.day),
        hour: r.hour,
        soc:  Math.max(0, Math.min(100, r.avg_soc_pct ?? 50)),
      }));
    }
    const start = period?.start ? new Date(period.start) : new Date();
    const out = [];
    for (let d = 0; d < days; d++) {
      for (let h = 0; h < 24; h++) {
        const phase = Math.cos(((h - 6) / 24) * 2 * Math.PI);
        const base = 50 + phase * 30;
        const noise = (seed01(assetId || '_', period?.code || '_', `${d}-${h}`) - 0.5) * 10;
        const date = new Date(start);
        date.setDate(start.getDate() + d);
        out.push({ day: date, hour: h, soc: Math.max(5, Math.min(95, base + noise)) });
      }
    }
    return out;
  }, [socSeries, assetId, period, days]);

  // Colour ramp: low SoC = red, mid = amber, high = green.
  const cellColor = (soc) => {
    if (soc < 25) return '#ef4444';
    if (soc < 45) return '#f97316';
    if (soc < 65) return '#f59e0b';
    if (soc < 80) return '#84cc16';
    return '#22c55e';
  };

  return (
    <Card sx={{ p: 3, display: 'flex', flexDirection: 'column' }}>
      <Stack direction="row" justifyContent="space-between" alignItems="baseline" sx={{ mb: 0.5 }}>
        <Typography variant="overline" sx={{ color: 'text.secondary' }}>
          State of charge · hour × day
        </Typography>
        <Typography variant="caption" sx={{ color: 'text.disabled' }}>
          {days}d × 24h
        </Typography>
      </Stack>
      {loading ? (
        <Skeleton variant="rectangular" height={260} sx={{ bgcolor: colors.skeleton, borderRadius: 1 }} />
      ) : (
        <Box sx={{
          display: 'grid',
          gridTemplateColumns: `repeat(${days}, 1fr)`,
          gridTemplateRows: 'repeat(24, 1fr)',
          gap: '1px',
          height: 260,
          // Flip y so 00 is at the bottom — like a stock chart of energy.
          direction: 'ltr',
        }}>
          {cells.map((c, i) => (
            <Box
              key={i}
              title={`${c.day.toLocaleDateString('en-AU', { day: '2-digit', month: 'short' })} · ${String(c.hour).padStart(2, '0')}:00 — SoC ${c.soc.toFixed(0)}%`}
              sx={{
                gridColumn: Math.floor(i / 24) + 1,
                gridRow: 24 - (i % 24),    // invert so morning at bottom feels wrong; show top→bottom by hour
                bgcolor: cellColor(c.soc),
                opacity: 0.85,
              }}
            />
          ))}
        </Box>
      )}
      {/* Legend */}
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 1.25 }}>
        <Typography variant="caption" sx={{ color: 'text.disabled' }}>Low SoC</Typography>
        {['#ef4444', '#f97316', '#f59e0b', '#84cc16', '#22c55e'].map((c, i) => (
          <Box key={i} sx={{ width: 18, height: 8, bgcolor: c, borderRadius: '2px' }} />
        ))}
        <Typography variant="caption" sx={{ color: 'text.disabled' }}>High SoC</Typography>
      </Stack>
    </Card>
  );
}


// ── CapacityFactorTrendCard ────────────────────────────────────────────────
// Consumes the per-day CF series from atlas.capacity_factor_daily when the
// /technical endpoint has responded; falls back to a seeded synthetic
// series anchored on the real period-avg CF while the request is in flight.
function CapacityFactorTrendCard({
  cfSeries, assetId, period, currentCfPct, loading, colors,
}) {
  const days = periodDays(period);
  const series = useMemo(() => {
    if (cfSeries && cfSeries.length) {
      return cfSeries
        .filter((r) => r.day)
        .map((r) => ({ day: new Date(r.day), value: r.cf_pct ?? 0 }));
    }
    // Fallback while real data is loading — synthetic jitter around the
    // period average so the chart isn't blank during the brief gap.
    const base = currentCfPct > 0 ? currentCfPct : 25;
    return stubSeries(assetId, period, 'cf', days, base, base * 0.25);
  }, [cfSeries, assetId, period, days, currentCfPct]);

  // LTM-average overlay — once /performance/yoy is wired we can pull a true
  // 12-month rolling average per DUID. Today the headline CF doubles as a
  // sensible reference line so the trend has something to read against.
  const ltm = currentCfPct;

  return (
    <Card sx={{ p: 3 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="baseline" sx={{ mb: 0.5 }}>
        <Typography variant="overline" sx={{ color: 'text.secondary' }}>
          Capacity factor trend
        </Typography>
        <Typography variant="caption" sx={{ color: 'text.disabled' }}>
          LTM avg overlay
        </Typography>
      </Stack>
      {loading ? (
        <Skeleton variant="rectangular" height={200} sx={{ bgcolor: colors.skeleton, borderRadius: 1 }} />
      ) : (
        <LineChart
          xAxis={[{
            data: series.map((s) => s.day),
            scaleType: 'time',
            valueFormatter: (v) => new Date(v).toLocaleDateString('en-AU', { day: 'numeric', month: 'short' }),
            tickLabelStyle: { fontSize: 10, fill: colors.textDim },
          }]}
          yAxis={[{ valueFormatter: (v) => `${v.toFixed(0)}%`,
                    tickLabelStyle: { fontSize: 10, fill: colors.textDim } }]}
          series={[
            { data: series.map((s) => s.value), color: '#3b82f6',
              label: 'Daily CF', curve: 'monotoneX', showMark: false,
              valueFormatter: (v) => `${v.toFixed(1)}%` },
            { data: series.map(() => ltm), color: '#94a3b8',
              label: 'LTM avg', curve: 'linear', showMark: false,
              valueFormatter: (v) => `${v.toFixed(1)}%` },
          ]}
          height={220}
          margin={{ top: 16, right: 8, bottom: 28, left: 36 }}
          slotProps={{
            legend: {
              direction: 'row',
              position: { vertical: 'bottom', horizontal: 'middle' },
              labelStyle: { fontSize: 10, fill: colors.textDim },
            },
          }}
        />
      )}
    </Card>
  );
}


// ── DispatchComplianceCard ─────────────────────────────────────────────────
// Real per-day series via atlas.dispatch_compliance_daily (5% threshold);
// falls back to a seeded synthetic series anchored on the period KPI while
// the request is in flight.
function DispatchComplianceCard({
  dailySeries, assetId, period, currentPct, loading, colors,
}) {
  const days = periodDays(period);
  const series = useMemo(() => {
    if (dailySeries && dailySeries.length) {
      return dailySeries
        .filter((r) => r.day && r.compliance_pct != null)
        .map((r) => ({ day: new Date(r.day), value: r.compliance_pct }));
    }
    const base = currentPct > 0 ? currentPct : 98;
    return stubSeries(assetId, period, 'dc', days, base, 1.5);
  }, [dailySeries, assetId, period, days, currentPct]);

  // Period average of the plotted daily series — shown beside the target so
  // it's a one-glance read on whether the asset cleared the 95% bar.
  const avgPct = useMemo(() => {
    if (!series.length) return null;
    return series.reduce((sum, p) => sum + p.value, 0) / series.length;
  }, [series]);

  return (
    <Card sx={{ p: 3 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="baseline" sx={{ mb: 0.5 }}>
        <Typography variant="overline" sx={{ color: 'text.secondary' }}>
          Dispatch compliance
        </Typography>
        <Stack direction="row" spacing={1.5} alignItems="baseline">
          {!loading && avgPct != null && (
            <Typography
              variant="caption"
              sx={{ color: avgPct >= 95 ? '#22c55e' : '#ef4444', fontWeight: 600 }}
            >
              avg: {avgPct.toFixed(2)}%
            </Typography>
          )}
          <Typography variant="caption" sx={{ color: 'text.disabled' }}>
            target: 95%
          </Typography>
        </Stack>
      </Stack>
      {loading ? (
        <Skeleton variant="rectangular" height={200} sx={{ bgcolor: colors.skeleton, borderRadius: 1 }} />
      ) : (
        <LineChart
          xAxis={[{
            data: series.map((s) => s.day),
            scaleType: 'time',
            valueFormatter: (v) => new Date(v).toLocaleDateString('en-AU', { day: 'numeric', month: 'short' }),
            tickLabelStyle: { fontSize: 10, fill: colors.textDim },
          }]}
          yAxis={[{ min: Math.max(85, Math.min(...series.map((s) => s.value)) - 2),
                    max: 100, valueFormatter: (v) => `${v.toFixed(0)}%`,
                    tickLabelStyle: { fontSize: 10, fill: colors.textDim } }]}
          series={[
            { data: series.map((s) => s.value), color: '#06b6d4',
              label: 'Compliance', curve: 'monotoneX', showMark: false, area: true,
              valueFormatter: (v) => `${v.toFixed(2)}%` },
            { data: series.map(() => 95), color: '#94a3b8',
              label: 'Target', curve: 'linear', showMark: false,
              valueFormatter: () => '95%' },
          ]}
          height={220}
          margin={{ top: 16, right: 8, bottom: 28, left: 36 }}
          slotProps={{
            legend: {
              direction: 'row',
              position: { vertical: 'bottom', horizontal: 'middle' },
              labelStyle: { fontSize: 10, fill: colors.textDim },
            },
          }}
        />
      )}
    </Card>
  );
}


// ── CycleLedgerCard ────────────────────────────────────────────────────────
// Real per-day EFC from atlas.bess_cycles_rte_daily when available.
function CycleLedgerCard({
  cyclesSeries, assetId, period, warrantyAnnual, loading, colors,
}) {
  const days = periodDays(period);
  const budgetPerDay = warrantyAnnual / 365;
  const series = useMemo(() => {
    if (cyclesSeries && cyclesSeries.length) {
      return cyclesSeries
        .filter((r) => r.day)
        .map((r) => ({ day: new Date(r.day), value: r.efc ?? 0 }));
    }
    return stubSeries(assetId, period, 'cyc', days, 1.6, 0.7);
  }, [cyclesSeries, assetId, period, days]);

  return (
    <Card sx={{ p: 3 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="baseline" sx={{ mb: 0.5 }}>
        <Typography variant="overline" sx={{ color: 'text.secondary' }}>
          Cycle ledger
        </Typography>
        <Typography variant="caption" sx={{ color: 'text.disabled' }}>
          budget: {budgetPerDay.toFixed(1)} /day
        </Typography>
      </Stack>
      {loading ? (
        <Skeleton variant="rectangular" height={200} sx={{ bgcolor: colors.skeleton, borderRadius: 1 }} />
      ) : (
        <BarChart
          xAxis={[{
            scaleType: 'band',
            data: series.map((s) => s.day),
            valueFormatter: (v) => new Date(v).toLocaleDateString('en-AU', { day: 'numeric', month: 'short' }),
            tickLabelStyle: { fontSize: 10, fill: colors.textDim },
          }]}
          yAxis={[{ valueFormatter: (v) => v.toFixed(1),
                    tickLabelStyle: { fontSize: 10, fill: colors.textDim } }]}
          series={[
            { data: series.map((s) => Math.max(0, s.value)), color: '#8b5cf6',
              label: 'EFC', valueFormatter: (v) => v?.toFixed?.(2) },
          ]}
          height={220}
          margin={{ top: 16, right: 8, bottom: 28, left: 36 }}
          slotProps={{
            legend: {
              direction: 'row',
              position: { vertical: 'bottom', horizontal: 'middle' },
              labelStyle: { fontSize: 10, fill: colors.textDim },
            },
          }}
        />
      )}
    </Card>
  );
}


// ── RteTrendCard ───────────────────────────────────────────────────────────
// Real per-day RTE from atlas.bess_cycles_rte_daily — same series as the
// CycleLedger uses, just reading rte_pct instead of efc.
function RteTrendCard({
  cyclesSeries, assetId, period, designRte, loading, colors,
}) {
  const days = periodDays(period);
  const series = useMemo(() => {
    if (cyclesSeries && cyclesSeries.length) {
      return cyclesSeries
        .filter((r) => r.day && r.rte_pct != null)
        .map((r) => ({ day: new Date(r.day), value: r.rte_pct }));
    }
    return stubSeries(assetId, period, 'rte_t', days, 86, 1.5, -1.5);  // slight downward drift
  }, [cyclesSeries, assetId, period, days]);

  return (
    <Card sx={{ p: 3 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="baseline" sx={{ mb: 0.5 }}>
        <Typography variant="overline" sx={{ color: 'text.secondary' }}>
          Round-trip efficiency trend
        </Typography>
        <Typography variant="caption" sx={{ color: 'text.disabled' }}>
          design: {designRte}%
        </Typography>
      </Stack>
      {loading ? (
        <Skeleton variant="rectangular" height={200} sx={{ bgcolor: colors.skeleton, borderRadius: 1 }} />
      ) : (
        <LineChart
          xAxis={[{
            data: series.map((s) => s.day),
            scaleType: 'time',
            valueFormatter: (v) => new Date(v).toLocaleDateString('en-AU', { day: 'numeric', month: 'short' }),
            tickLabelStyle: { fontSize: 10, fill: colors.textDim },
          }]}
          yAxis={[{ valueFormatter: (v) => `${v.toFixed(0)}%`,
                    min: 80, max: 92,
                    tickLabelStyle: { fontSize: 10, fill: colors.textDim } }]}
          series={[
            { data: series.map((s) => s.value), color: '#22c55e',
              label: 'RTE', curve: 'monotoneX', showMark: false, area: true,
              valueFormatter: (v) => `${v.toFixed(1)}%` },
            { data: series.map(() => designRte), color: '#94a3b8',
              label: 'Design', curve: 'linear', showMark: false,
              valueFormatter: (v) => `${v}%` },
          ]}
          height={220}
          margin={{ top: 16, right: 8, bottom: 28, left: 36 }}
          slotProps={{
            legend: {
              direction: 'row',
              position: { vertical: 'bottom', horizontal: 'middle' },
              labelStyle: { fontSize: 10, fill: colors.textDim },
            },
          }}
        />
      )}
    </Card>
  );
}


// ── FcasEnablementCard ─────────────────────────────────────────────────────
// Stacked-bar of FCAS enablement hours per bucket, per service. Three
// precedence levels:
//   1. REAL bucketed series from atlas.fcas_enablement_series (column-shape
//      payload from /technical.sections.fcas_enablement_series).
//   2. Period-level interval-count shape from atlas.fcas_enablement, smeared
//      across days deterministically (used briefly while #1 is loading).
//   3. Revenue-share fallback from /commercial's bess_revenue_stack, also
//      smeared (only fires when /technical is unavailable).
function FcasEnablementCard({
  fcasSeries, fcasEnablement, fcasComponents, assetId, period, loading, colors,
}) {
  const days = periodDays(period);
  const stackedSeries = useMemo(() => {
    // ── 1. REAL bucketed series ────────────────────────────────────────
    // Column shape: { bucket_minutes, buckets:[ISO,...],
    //                 services:[{key,label,category,hrs:[...]}], any_hrs:[...] }.
    if (fcasSeries?.buckets?.length && fcasSeries?.services?.length) {
      const xs = fcasSeries.buckets.map((b) => new Date(b));
      // Keep only RAISE/LOWER services with any hours so the legend stays
      // tight; cap at 6 to match the colour palette and chart legibility.
      const seriesData = fcasSeries.services
        .filter((s) => s.key?.match(/^(RAISE|LOWER)/))
        .filter((s) => (s.hrs || []).some((h) => h > 0))
        .slice(0, 6)
        .map((s) => ({
          key:   s.key,
          label: s.key,
          color: FCAS_COLOR[s.key] || '#a78bfa',
          data:  s.hrs || [],
        }));
      if (seriesData.length) {
        return { xs, seriesData, bucketMinutes: fcasSeries.bucket_minutes };
      }
    }

    // ── 2 + 3. Fallback shape (used during in-flight only) ─────────────
    const realServices = (fcasEnablement?.by_service || [])
      .filter((s) => (s.enablement_pct || 0) > 0 && s.key?.match(/^(RAISE|LOWER)/));
    const fallbackServices = (fcasComponents || [])
      .filter((c) => (c.aud || 0) > 0 && c.key?.match(/^(RAISE|LOWER)/));
    const useReal = realServices.length > 0;
    const services = useReal ? realServices : fallbackServices;
    if (!services.length) return null;

    const xs = Array.from({ length: days }, (_, i) => {
      const d = new Date(period?.start || new Date());
      d.setDate(d.getDate() + i);
      return d;
    });
    const totalShare = useReal
      ? services.reduce((a, s) => a + (s.enablement_pct || 0), 0) || 1
      : services.reduce((a, c) => a + (c.aud || 0), 0) || 1;
    const seriesData = services.slice(0, 6).map((s) => {
      const share = useReal
        ? (s.enablement_pct || 0) / totalShare
        : (s.aud || 0) / totalShare;
      return {
        key: s.key,
        label: s.key,
        color: FCAS_COLOR[s.key] || '#a78bfa',
        data: xs.map((_, i) =>
          Math.max(0, 24 * share * (0.7 + 0.6 * seed01(assetId, period?.code, s.key, String(i))))
        ),
      };
    });
    return { xs, seriesData, bucketMinutes: 1440 };
  }, [fcasSeries, fcasEnablement, fcasComponents, assetId, period, days]);

  return (
    <Card sx={{ p: 3 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="baseline" sx={{ mb: 0.5 }}>
        <Typography variant="overline" sx={{ color: 'text.secondary' }}>
          FCAS enablement
        </Typography>
      </Stack>
      {loading ? (
        <Skeleton variant="rectangular" height={200} sx={{ bgcolor: colors.skeleton, borderRadius: 1 }} />
      ) : !stackedSeries ? (
        <Box sx={{ height: 220, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Typography color="text.disabled">No FCAS enablement in this window.</Typography>
        </Box>
      ) : (
        (() => {
          // Granularity-aware x-axis labelling — match the formatter used
          // on Commercial Performance so 5-min buckets show HH:MM, monthly
          // buckets show "Apr 2026", etc. yMax also scales with bucket
          // size so 30-min FCAS hours (≤0.5/bucket) don't get crushed.
          const m = stackedSeries.bucketMinutes || 1440;
          // timeZone:'UTC' on every variant — timestamps are NEM-clock-as-
          // UTC, so rendering them in the browser's zone shifts the labels
          // (+1 h in the UK, +8 in Singapore, +10 in Sydney).
          const tickFmt = (v) => {
            const d = v instanceof Date ? v : new Date(v);
            if (m <   60) return d.toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit', hour12: false, timeZone: 'UTC' });
            if (m < 1440) return d.toLocaleString('en-AU', { day: 'numeric', month: 'short', hour: '2-digit', hour12: false, timeZone: 'UTC' });
            if (m <= 10080) return d.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', timeZone: 'UTC' });
            return d.toLocaleDateString('en-AU', { month: 'short', year: 'numeric', timeZone: 'UTC' });
          };
          // y-axis max = bucket-hours so a fully-enabled bucket lands at 100%.
          const yMax = m / 60;
          return (
            <BarChart
              xAxis={[{
                scaleType: 'band',
                data: stackedSeries.xs,
                valueFormatter: tickFmt,
                tickLabelStyle: { fontSize: 10, fill: colors.textDim },
              }]}
              yAxis={[{ max: yMax,
                        valueFormatter: (v) => v >= 1 ? `${v}h` : `${(v*60).toFixed(0)}m`,
                        tickLabelStyle: { fontSize: 10, fill: colors.textDim } }]}
              series={stackedSeries.seriesData.map((s) => ({
                data: s.data, stack: 'f', color: s.color, label: s.label,
                valueFormatter: (v) =>
                  v == null ? null
                  : v >= 1   ? `${v.toFixed(2)} h`
                  :            `${(v * 60).toFixed(0)} min`,
              }))}
              height={220}
              margin={{ top: 16, right: 8, bottom: 28, left: 36 }}
              slotProps={{
                legend: {
                  direction: 'row',
                  position: { vertical: 'bottom', horizontal: 'middle' },
                  labelStyle: { fontSize: 10, fill: colors.textDim },
                },
              }}
            />
          );
        })()
      )}
    </Card>
  );
}


// ── ConstraintParetoCard ──────────────────────────────────────────────────
// Moved to its own file (ConstraintParetoCard.js) and rendered at the bottom
// of the Curtailment tab — the 2026-06-10 METIS review moved the binding-
// constraints table into the curtailment story. The hour-of-day generation
// curve that used to follow it was killed in the same review (a solar
// profile is always the same shape).


// ── ProductionByThresholdCard ──────────────────────────────────────────────
// Moved in from the Spot Price tab (ClearingPrice.js) per the 2026-06-10
// METIS review — "this is a production thing", so it lives on Technical.
// Splits the asset's generated MWh into bands against two thresholds:
//   cyan  — RRP > Threshold 1
//   amber — T1−T2 < RRP ≤ T1   (clears once Threshold 2 is netted off)
//   grey  — RRP ≤ T1−T2        (uneconomic even net of T2)
// Each threshold is either a number OR a live series (2026-06-11: "selection
// of series for RRP and LGC"). Today the one series is the daily Spot LGC
// from aus_warehouse.lgc_price; pick it on either threshold to make that line
// track the LGC as-of each day. Defaults preserve the prior behaviour:
// T1 = number $0, T2 = number = current spot LGC.
function ThresholdControl({ label, hint, mode, onMode, draft, onDraft, onCommit }) {
  return (
    <Stack direction="row" spacing={0.75} alignItems="center">
      <Tooltip title={hint}>
        <Typography variant="caption" sx={{ color: 'text.secondary', whiteSpace: 'nowrap', cursor: 'help' }}>
          {label}
        </Typography>
      </Tooltip>
      <Select
        size="small"
        value={mode}
        onChange={(e) => onMode(e.target.value)}
        sx={{ '& .MuiSelect-select': { fontSize: '0.8rem', py: 0.9 } }}
      >
        <MenuItem value="number">Number</MenuItem>
        <MenuItem value="lgc">Spot LGC</MenuItem>
      </Select>
      {mode === 'number' && (
        <TextField
          size="small"
          value={draft}
          onChange={(e) => onDraft(e.target.value)}
          onBlur={onCommit}
          onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
          InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
          sx={{ width: 104 }}
        />
      )}
    </Stack>
  );
}

function ProductionByThresholdCard({ portfolioId, assetId, period, colors }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  // Threshold 1 — the RRP price line. number (default $0) or Spot LGC series.
  const [t1Mode, setT1Mode] = useState('number');
  const [rrpOverride, setRrpOverride] = useState('');   // '' = default $0
  const [rrpDraft, setRrpDraft] = useState('0');
  // Threshold 2 — the offset netted off T1. number (default current spot LGC)
  // or Spot LGC series.
  const [t2Mode, setT2Mode] = useState('number');
  const [lgcOverride, setLgcOverride] = useState('');   // '' = use API default
  const [lgcDraft, setLgcDraft] = useState('');

  const bucket = useMemo(() => {
    const f = period?.frequency;
    if (f === '5min' || f === '30min' || f === 'hourly') return 'hourly';
    if (f === 'weekly') return 'weekly';
    if (f === 'monthly' || f === 'yearly') return 'monthly';
    return 'daily';
  }, [period]);

  useEffect(() => {
    if (!assetId) { setData(null); return; }
    setData(null);
    setError(null);
    // Explicit picker window — same instants every other card queries.
    const params = {
      ...periodApiParams(period, { withFrequency: false }),
      asset_id: assetId,
      bucket,
    };
    // Each threshold: series flag when in 'lgc' mode, else its number. We
    // still send the number alongside a series flag — the backend uses it as
    // the carry-forward fallback for days with no LGC quote.
    if (t1Mode === 'lgc') params.t1_series = 'lgc';
    if (rrpOverride !== '' && !Number.isNaN(Number(rrpOverride))) {
      params.rrp_threshold = Number(rrpOverride);
    }
    if (t2Mode === 'lgc') params.t2_series = 'lgc';
    if (lgcOverride !== '' && !Number.isNaN(Number(lgcOverride))) {
      params.lgc = Number(lgcOverride);
    }
    let cancelled = false;
    cachedGet(`${API_BASE_URL}/api/portfolios/${portfolioId}/clearing-price`, { params })
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch((e) => { if (!cancelled) setError(e.response?.data?.error || e.message); });
    return () => { cancelled = true; };
  }, [portfolioId, assetId, period, bucket, t1Mode, t2Mode, lgcOverride, rrpOverride]);

  // Keep the T2 number field synced to the API-resolved spot LGC until the
  // user commits an override; after that the override drives the fetches.
  useEffect(() => {
    if (lgcOverride === '' && data?.lgc?.aud != null) {
      setLgcDraft(String(data.lgc.aud));
    }
  }, [data?.lgc?.aud, lgcOverride]);

  const series = data?.series || [];
  const xDates = series.map((r) => new Date(r.date));
  // Two stacked bands sum to "RRP > T1−T2"; the grey residual is generation
  // at RRP ≤ T1−T2 (uneconomic even net of Threshold 2).
  const aboveZero = series.map((r) => r.mwh_above_zero || 0);
  const lgcOnly = series.map(
    (r) => Math.max(0, (r.mwh_above_neg_lgc || 0) - (r.mwh_above_zero || 0)),
  );
  const belowNeg = series.map(
    (r) => Math.max(0, (r.generation_mwh || 0) - (r.mwh_above_neg_lgc || 0)),
  );
  const totals = data?.totals;

  // Band labels reflect each threshold's resolved value/mode so the legend
  // never lies about what the bands mean.
  const t1Disp = t1Mode === 'lgc' ? 'Spot LGC' : `$${Number(data?.rrp_threshold ?? 0)}`;
  const t2Disp = t2Mode === 'lgc' ? 'Spot LGC' : `$${Number(data?.lgc?.aud ?? 0)}`;
  const netLabel = `${t1Disp} − ${t2Disp}`;

  return (
    <Card sx={{ p: 3 }}>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ mb: 1, flexWrap: 'wrap', rowGap: 1 }}
      >
        <Typography variant="overline" sx={{ color: 'text.secondary' }}>
          Production by threshold
        </Typography>
        <Stack direction="row" spacing={2} sx={{ flexWrap: 'wrap', rowGap: 1 }}>
          <ThresholdControl
            label="Threshold 1"
            hint="Threshold 1 — the price line production is measured against. A number (default $0 — raise it to your PPA strike, floor, etc.) or the daily Spot LGC series."
            mode={t1Mode}
            onMode={setT1Mode}
            draft={rrpDraft}
            onDraft={setRrpDraft}
            onCommit={() => {
              if (rrpDraft === '') { setRrpOverride(''); setRrpDraft('0'); }
              else if (!Number.isNaN(Number(rrpDraft))) setRrpOverride(rrpDraft);
            }}
          />
          <ThresholdControl
            label="Threshold 2"
            hint="Threshold 2 — netted off Threshold 1 for the middle band. A number (defaults to current spot LGC from aus_warehouse.lgc_price) or the daily Spot LGC series."
            mode={t2Mode}
            onMode={setT2Mode}
            draft={lgcDraft}
            onDraft={setLgcDraft}
            onCommit={() => {
              if (lgcDraft === '') setLgcOverride('');
              else if (!Number.isNaN(Number(lgcDraft))) setLgcOverride(lgcDraft);
            }}
          />
        </Stack>
      </Stack>

      {/* Band headline strip — totals + share of MWh per band */}
      {totals && (
        <Stack direction="row" spacing={3} sx={{ mb: 1, flexWrap: 'wrap', rowGap: 0.5 }}>
          {[
            { label: 'All generation', mwh: totals.generation_mwh, pct: null, color: '#94a3b8' },
            { label: `RRP > ${t1Disp}`, mwh: totals.mwh_above_zero, pct: totals.pct_mwh_above_zero, color: '#22d3ee' },
            { label: `RRP > ${netLabel}`, mwh: totals.mwh_above_neg_lgc, pct: totals.pct_mwh_above_neg_lgc, color: '#d98a2b' },
          ].map((b) => (
            <Stack key={b.label} direction="row" alignItems="center" spacing={0.75}>
              <Box sx={{ width: 10, height: 10, borderRadius: '2px', bgcolor: b.color }} />
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>{b.label}</Typography>
              <Typography variant="caption" sx={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
                {fmtMWhCompact(b.mwh ?? 0)}{b.pct != null ? ` · ${Number(b.pct).toFixed(1)}%` : ''}
              </Typography>
            </Stack>
          ))}
        </Stack>
      )}

      {error ? (
        <Box sx={{ height: 320, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Typography color="text.disabled">{error}</Typography>
        </Box>
      ) : !data ? (
        <Skeleton variant="rectangular" height={320} sx={{ bgcolor: colors.skeleton, borderRadius: 1 }} />
      ) : !series.length ? (
        <Box sx={{ height: 320, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Typography color="text.disabled">No generation in this period.</Typography>
        </Box>
      ) : (
        <BarChart
          height={320}
          xAxis={[{
            data: xDates,
            scaleType: 'band',
            valueFormatter: (v) => {
              const d = v instanceof Date ? v : new Date(v);
              return d.toLocaleDateString('en-AU', { day: '2-digit', month: 'short' });
            },
            tickLabelStyle: { fontSize: 10, fill: colors.textDim },
          }]}
          yAxis={[{
            valueFormatter: (v) => fmtMWhCompact(v),
            tickLabelStyle: { fontSize: 10, fill: colors.textDim },
          }]}
          series={[
            { data: aboveZero, label: `RRP > ${t1Disp}`, stack: 'g', color: '#22d3ee',
              valueFormatter: (v) => fmtMWhCompact(v ?? 0) },
            { data: lgcOnly, label: `${netLabel} < RRP ≤ ${t1Disp}`, stack: 'g', color: '#d98a2b',
              valueFormatter: (v) => fmtMWhCompact(v ?? 0) },
            { data: belowNeg, label: `RRP ≤ ${netLabel} (uneconomic)`, stack: 'g', color: '#cbd5e1',
              valueFormatter: (v) => fmtMWhCompact(v ?? 0) },
          ]}
          margin={{ top: 16, right: 16, bottom: 48, left: 56 }}
          slotProps={{
            legend: {
              direction: 'row',
              position: { vertical: 'bottom', horizontal: 'middle' },
              labelStyle: { fontSize: 10, fill: colors.textDim },
            },
          }}
        />
      )}
    </Card>
  );
}


// ── HourOfDayBatteryCard ──────────────────────────────────────────────────
// REAL data: bucket the per-DUID dispatch series by hour-of-day. For BESS
// DUIDs, DISPATCHLOAD.TOTALCLEARED is signed — positive = discharging,
// negative = charging — so the same dispatch endpoint that drives the
// generation hour-of-day curve gives us the duck-curve directly.
function HourOfDayBatteryCard({
  dispatchData, assetId, period, capacityMw, loading, colors,
}) {
  const data = useMemo(() => {
    const series = dispatchData?.series || [];
    if (!series.length) return [];
    const buckets = Array.from({ length: 24 }, () => ({ sum: 0, n: 0 }));
    for (const p of series) {
      if (p.mw == null) continue;
      // SETTLEMENTDATE is NEM-clock-as-UTC end to end (warehouse convention
      // — never tz-convert). `t` arrives as "…T11:00:00+00:00" where 11:00
      // IS the NEM wall-clock hour, so read it back with the UTC accessor.
      // Converting to Australia/Sydney here (the pre-2026-06-10 bug) added
      // +10 h and shifted the whole profile — solar's twin card peaked at
      // 21:00 / "7 o'clock" depending on the viewer's browser timezone.
      const safeHour = new Date(p.t).getUTCHours();
      buckets[safeHour].sum += Number(p.mw);
      buckets[safeHour].n += 1;
    }
    return buckets.map((b, h) => ({
      hour: h,
      mw: b.n > 0 ? b.sum / b.n : 0,
    }));
  }, [dispatchData]);

  return (
    <Card sx={{ p: 3 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="baseline" sx={{ mb: 0.5 }}>
        <Typography variant="overline" sx={{ color: 'text.secondary' }}>
          Charge / discharge by hour-of-day
        </Typography>
        <Typography variant="caption" sx={{ color: 'text.disabled' }}>
          + discharge / − charge
        </Typography>
      </Stack>
      {loading ? (
        <Skeleton variant="rectangular" height={200} sx={{ bgcolor: colors.skeleton, borderRadius: 1 }} />
      ) : !data.length ? (
        <Box sx={{ height: 220, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Typography color="text.disabled">No dispatch data for this asset / period.</Typography>
        </Box>
      ) : (
        <BarChart
          xAxis={[{
            scaleType: 'band',
            data: data.map((d) => String(d.hour).padStart(2, '0')),
            tickLabelStyle: { fontSize: 10, fill: colors.textDim },
          }]}
          yAxis={[{ valueFormatter: (v) => `${Math.round(v)} MW`,
                    tickLabelStyle: { fontSize: 10, fill: colors.textDim } }]}
          series={[
            // Discharge (positive) and charge (negative) as separate series
            // so they can have different colours.
            { data: data.map((d) => Math.max(0, d.mw)), color: '#22c55e',
              label: 'Discharge',
              valueFormatter: (v) => v > 0 ? `${v.toFixed(1)} MW` : null },
            { data: data.map((d) => Math.min(0, d.mw)), color: '#E9B065',
              label: 'Charge',
              valueFormatter: (v) => v < 0 ? `${(-v).toFixed(1)} MW` : null },
          ]}
          height={220}
          margin={{ top: 16, right: 8, bottom: 28, left: 48 }}
          slotProps={{
            legend: {
              direction: 'row',
              position: { vertical: 'top', horizontal: 'right' },
              labelStyle: { fontSize: 10, fill: colors.textDim },
            },
          }}
        />
      )}
    </Card>
  );
}


// ── SetpointDeviationCard ─────────────────────────────────────────────────
// Histogram of |INITIALMW − TOTALCLEARED| ÷ capacity. Real bins now come
// from atlas.setpoint_deviation_histogram via /technical; falls back to a
// compliance-weighted synthetic distribution if the request is still in
// flight or the asset is missing INITIALMW.
function SetpointDeviationCard({
  histogram, assetId, period, setpointCompliancePct, loading, colors,
}) {
  // 7 canonical bins; the atlas function returns these exact labels in this
  // exact order so the bin set matches what the chart expects.
  const bins = useMemo(() => {
    const labels = ['0–0.5%', '0.5–1%', '1–2%', '2–5%', '5–10%', '10–20%', '>20%'];

    // REAL path — index the histogram by bucket label.
    if (histogram && histogram.length) {
      const byBucket = Object.fromEntries(
        histogram.map((b) => [b.bucket, b.count || 0])
      );
      return labels.map((l, i) => ({
        label: l,
        count: byBucket[l] || 0,
        isOk: i < 2,   // first two bins meet the 2% spec
      }));
    }

    // Fallback — synthetic, scaled by the headline compliance %.
    const comp = setpointCompliancePct / 100;
    const ramp = 1 - comp;
    const weights = [
      0.55 - 0.25 * ramp,
      0.25 - 0.05 * ramp,
      0.10 + 0.05 * ramp,
      0.06 + 0.10 * ramp,
      0.02 + 0.10 * ramp,
      0.015 + 0.05 * ramp,
      0.005 + 0.05 * ramp,
    ];
    const sum = weights.reduce((a, b) => a + b, 0);
    return labels.map((l, i) => ({
      label: l,
      count: Math.round((weights[i] / sum) * 1000),
      isOk: i < 2,
    }));
  }, [histogram, setpointCompliancePct]);

  return (
    <Card sx={{ p: 3 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="baseline" sx={{ mb: 0.5 }}>
        <Typography variant="overline" sx={{ color: 'text.secondary' }}>
          Setpoint deviation distribution
        </Typography>
        <Typography variant="caption" sx={{ color: 'text.disabled' }}>
          |actual − target| / capacity
        </Typography>
      </Stack>
      {loading ? (
        <Skeleton variant="rectangular" height={200} sx={{ bgcolor: colors.skeleton, borderRadius: 1 }} />
      ) : (
        <BarChart
          xAxis={[{
            scaleType: 'band',
            data: bins.map((b) => b.label),
            colorMap: {
              type: 'ordinal',
              values: bins.map((b) => b.label),
              colors: bins.map((b, i) =>
                b.isOk ? '#22c55e'
                  : i < 4 ? '#f59e0b'
                  :         '#ef4444'),
            },
            tickLabelStyle: { fontSize: 10, fill: colors.textDim },
          }]}
          yAxis={[{ label: 'Intervals',
                    tickLabelStyle: { fontSize: 10, fill: colors.textDim },
                    labelStyle: { fontSize: 11, fill: colors.textDim } }]}
          series={[{
            data: bins.map((b) => b.count),
            label: 'Intervals',
            valueFormatter: (v) => fmtInt(v),
          }]}
          height={220}
          margin={{ top: 16, right: 8, bottom: 36, left: 48 }}
          hideLegend
        />
      )}
    </Card>
  );
}
